import React, { ReactNode } from "react";
import { GetServerSideProps, NextPage } from "next";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";

import { getDeals } from "api/requests/getDeals";
import getPromotedStrains from "api/requests/getPromotedStrains";
import { getPromotions } from "api/requests/getPromotions";
import { getUserLocationData } from "api/requests/getUserLocation";
import { Action, Category } from "constants/events";
import {
  AdMarqueePageLocation,
  BACKGROUND_IMAGE_DESKTOP_HEIGHT,
  BACKGROUND_IMAGE_DESKTOP_WIDTH,
  BACKGROUND_IMAGE_MOBILE_HEIGHT,
  BACKGROUND_IMAGE_MOBILE_WIDTH,
} from "custom-types/AdMarquee";
import { Deal } from "custom-types/Deals";
import { PromotedStrain } from "custom-types/Directus";
import { Promotion } from "custom-types/Promotions";
import useDomain from "hooks/useDomain";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";
import useFetchMarqueeCreatives from "hooks/useFetchMarqueeCreatives";
import { getCountryCodeHeader } from "utils/requestContext";

import AdMarqueeCarousel from "components/AdMarqueeCarousel";
import BogosCarousel from "components/BogosCarousel";
import Breadcrumb from "components/Breadcrumb";
import Breadcrumbs from "components/Breadcrumbs";
import DealLocations from "components/DealLocations";
import DealsDisclaimer from "components/DealsDisclaimer";
import DealsHero from "components/DealsHero";
import DealsResults from "components/DealsResults";
import DealTypeCarousels from "components/DealTypeCarousels";
import LocationPickerButton from "components/LocationPickerButton";
import PromoResults from "components/PromoResults";
import TrackImpression from "components/TrackImpression";

// height of controls with margin in AdMarqueeCarousel
const CAROUSEL_CONTROLS_HEIGHT = 40;

type Props = {
  categories: string[];
  promotedStrains: PromotedStrain[];
  initialDeals: Deal[];
  initialBogos: Deal[];
  initialPromos: Promotion[];
};

const DealsPage: NextPage<Props> = ({
  categories,
  promotedStrains,
  initialDeals,
  initialBogos,
  initialPromos,
}) => {
  const { asPath } = useRouter();

  const countryCode = useDomainCountryCode();

  const baseUrl = useDomain();
  const { publishEvent } = useEventTracker();
  const { creatives, loading } = useFetchMarqueeCreatives("deals_page");

  const trackSeeAllDealsClick = () => {
    publishEvent({
      action: Action.click,
      category: Category.dealsMenuSeeAllLocations,
      label: "",
    });
  };

  const isCanada = countryCode === "CA";

  return (
    <div className="bg-white">
      <Head>
        <meta property="og:url" content={`${baseUrl}${asPath}`} key="og:url" />
        <meta
          key="description"
          name="description"
          content={
            isCanada
              ? "Looking for weed deals happening today? Find dispensary deals, special sales, promo codes, coupons, and discounts from cannabis shops in your area on Leafly."
              : "Looking for weed deals? Find special promotions & discounts on flower, dabs, carts, and edibles from dispensaries in your neighborhood on Leafly."
          }
        />
        <title>
          {isCanada
            ? "Cannabis Store Deals Near Me | Leafly"
            : "Dispensary Deals Near Me | Leafly"}
        </title>
        <link rel="canonical" href={`${baseUrl}/deals`} />
        <link
          href="https://www.leafly.com/deals "
          hrefLang="en-us"
          rel="alternate"
        />
        <link
          href="https://www.leafly.ca/deals "
          hrefLang="en-ca"
          rel="alternate"
        />
      </Head>
      <h1 className="text-lg container mt-lg mb-xl">
        {isCanada ? "Weed deals" : "Dispensary deals"}
      </h1>
      <div className="container">
        <div className="deals-hero-container flex items-center relative">
          {!loading &&
          creatives.some((c) => c.creativeType === "dispensary") ? (
            <AdMarqueeCarousel pageType={AdMarqueePageLocation.DEALS_PAGE} />
          ) : !loading ? (
            <DealsHero />
          ) : (
            <></>
          )}
        </div>
        <style jsx>{`
          .deals-hero-container {
            aspect-ratio: ${BACKGROUND_IMAGE_MOBILE_WIDTH} /
              ${BACKGROUND_IMAGE_MOBILE_HEIGHT + CAROUSEL_CONTROLS_HEIGHT};
          }
          @media (min-width: 768px) {
            .deals-hero-container {
              aspect-ratio: ${BACKGROUND_IMAGE_DESKTOP_WIDTH} /
                ${BACKGROUND_IMAGE_DESKTOP_HEIGHT + CAROUSEL_CONTROLS_HEIGHT};
            }
          }
        `}</style>
      </div>
      <BogosCarousel bogos={initialBogos} />
      {!!initialDeals?.length && (
        <>
          <DealSectionWrapper>
            <DealTypeCarousels
              categories={categories}
              promotedStrains={promotedStrains}
            />
          </DealSectionWrapper>

          <DealSectionWrapper>
            <DealsResults dealsProp={initialDeals} />
          </DealSectionWrapper>
        </>
      )}
      {!!initialPromos?.length && (
        <DealSectionWrapper>
          <PromoResults promotionsProp={initialPromos} />
        </DealSectionWrapper>
      )}
      {!initialDeals?.length && !initialPromos?.length && (
        <>
          <TrackImpression
            category="deals menu"
            action="impression"
            label="no deals nearby"
            threshold={0}
          >
            <DealSectionWrapper>
              <span data-testid="no-deals-no-promos">
                There aren't any deals in your area right now. You can learn
                about&nbsp;
              </span>
              <Link href="/strains/lists" className="underline">
                cannabis strains
              </Link>
              <span>,&nbsp;</span>
              <Link href="/dispensaries" className="underline">
                find dispensaries
              </Link>
              <span>&nbsp;near you, or change your location.</span>

              <LocationPickerButton />
              <DealLocations />
            </DealSectionWrapper>
          </TrackImpression>
        </>
      )}
      <section className="py-xxl">
        <div
          className="container"
          onClick={trackSeeAllDealsClick}
          onKeyPress={trackSeeAllDealsClick}
          role="link"
          tabIndex={0}
        >
          <p className="text-sm pb-xxl m-none">
            <Link href="/deals/locations">See all locations</Link>
          </p>
        </div>
        <p className="container">
          {isCanada
            ? "Cannabis retail stores in Canada are now able to offer exclusive dispensary deals and discounts using Leafly. Weed deals often come in different shapes and sizes ranging from first-time visit deals to veteran discounts, plus birthday deals and early bird specials. If you’re looking to save cash on cannabis (and who isn’t?), let Leafly help you get the biggest bang for your buck with our updated list of the hottest cannabis shop deals, sales, promo codes, and more near you."
            : "Cannabis dispensaries are now able to offer exclusive deals and discounts using Leafly. Weed deals often come in different shapes and sizes ranging from first-time dispensary deals to veteran discounts, plus birthday deals and early bird specials. If you’re looking to save cash on cannabis, let Leafly help you get the biggest bang for your buck with our updated list of the hottest deals near you happening now!"}
        </p>
      </section>

      <section className="border-t border-light-grey pt-xxl pb-lg">
        <div className="container">
          <DealsDisclaimer />
        </div>
        <div className="mt-md">
          <Breadcrumbs>
            <Breadcrumb currentLocation href="/deals">
              Deals
            </Breadcrumb>
          </Breadcrumbs>
        </div>
      </section>
    </div>
  );
};

const DealSectionWrapper: React.FC<{ children: ReactNode }> = ({
  children,
}) => (
  <section className="py-xxl border-b border-light-grey">
    <div className="container">{children}</div>
  </section>
);

export const getServerSideProps: GetServerSideProps = async (context) => {
  const countryCode = getCountryCodeHeader(context.req.headers);

  // getUserLocationData will return latitude and longitude no matter what
  // TODO: Remove type assertion once Location type has been solidified
  const location = await getUserLocationData(countryCode, context);
  const { latitude: lat, longitude: lon } = location.coordinates as {
    latitude: number;
    longitude: number;
  };

  const [promotedStrains, bogosResponse, promotionsResponse, dealsResponse] =
    await Promise.all([
      getPromotedStrains(),
      getDeals({ lat, lon }, countryCode, 0, 16, {
        "filter[kind]": "bogo",
      }),
      // load one more deal than initially visible so we can know if
      // there are more deals to be loaded without passing around totalCount
      getPromotions({ lat, lon }, countryCode, 0, 7),
      getDeals({ lat, lon }, countryCode, 0, 7),
    ]);

  return {
    props: {
      categories: dealsResponse.dealCategories,
      initialBogos: bogosResponse.deals,
      initialDeals: dealsResponse.deals,
      initialPromos: promotionsResponse.promotions,
      promotedStrains,
    },
  };
};

export default DealsPage;
